import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/**
 * LayoutContext — shared context for the persistent AppShell.
 *
 * Provides structural equality guards ignoring function references so calling
 * `setLayoutProps` with identical data values will not trigger infinite re-renders.
 */

function deepEqualIgnoreFunctions(a, b, seen = new WeakSet()) {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }
  if (React.isValidElement(a) || React.isValidElement(b)) {
    return a === b;
  }
  if (seen.has(a) || seen.has(b)) {
    return a === b;
  }
  seen.add(a);
  seen.add(b);
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqualIgnoreFunctions(a[i], b[i], seen)) return false;
    }
    return true;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (let i = 0; i < keysA.length; i++) {
    const key = keysA[i];
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (typeof a[key] === 'function' && typeof b[key] === 'function') {
      continue;
    } else if (!deepEqualIgnoreFunctions(a[key], b[key], seen)) {
      return false;
    }
  }
  return true;
}

export const THEME_STORAGE_KEY = 'app_theme';

export function resolveInitialTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
  } catch (e) {
    // ignore storage access errors
  }

  // Check system preference if available
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  }

  // Default to dark
  return 'dark';
}

const LayoutContext = createContext({
  headerProps: null,
  footerProps: null,
  setLayoutProps: () => {},
  selectShotForGeneration: null,
  themeMode: 'dark',
  setThemeMode: () => {},
  toggleThemeMode: () => {},
});

export function LayoutProvider({ children }) {
  const [headerProps, setHeaderProps] = useState(null);
  const [footerProps, setFooterProps] = useState(null);
  const [selectShotForGeneration, setSelectShotForGeneration] = useState(null);
  const [themeMode, setThemeModeState] = useState(resolveInitialTheme);

  // Sync data-theme attribute on documentElement
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', themeMode);
    }
  }, [themeMode]);

  // Listen to system preference changes if user has not explicitly set a preference
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleChange = (e) => {
      try {
        if (!localStorage.getItem(THEME_STORAGE_KEY)) {
          setThemeModeState(e.matches ? 'light' : 'dark');
        }
      } catch (err) {}
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (e) {}
  }, []);

  const toggleThemeMode = useCallback(() => {
    setThemeModeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch (e) {}
      return next;
    });
  }, []);

  const setLayoutProps = useCallback((config) => {
    if (!config) {
      setHeaderProps((prev) => (prev === null ? prev : null));
      setFooterProps((prev) => (prev === null ? prev : null));
      setSelectShotForGeneration(null);
    } else {
      if ('header' in config) {
        setHeaderProps((prev) => (deepEqualIgnoreFunctions(prev, config.header) ? prev : config.header));
      }
      if ('footer' in config) {
        setFooterProps((prev) => (deepEqualIgnoreFunctions(prev, config.footer) ? prev : config.footer));
      }
      if ('selectShotForGeneration' in config) {
        setSelectShotForGeneration(() => config.selectShotForGeneration);
      } else {
        setSelectShotForGeneration((prev) => (prev === null ? prev : null));
      }
    }
  }, []);

  const value = React.useMemo(
    () => ({
      headerProps,
      footerProps,
      setLayoutProps,
      selectShotForGeneration,
      themeMode,
      setThemeMode,
      toggleThemeMode,
    }),
    [headerProps, footerProps, setLayoutProps, selectShotForGeneration, themeMode, setThemeMode, toggleThemeMode]
  );

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayoutContext() {
  return useContext(LayoutContext);
}

export default LayoutContext;

